import { useEffect, useRef } from 'react';
import { trackExperimentViewed } from '../analytics/generic';
import { Services, useConsent, useLocale } from '../Runtime';
import {
  useHomeFirstViewportVariant,
  type HomeFirstViewportVariant,
} from './HomeFirstViewportVariant';

const EXPERIMENT_ID = 'index_first_viewport';

/** Akamai sets this cookie for users in the test (LPOT-1738). */
const ASSIGNMENT_COOKIE = 'ab_index';

const VARIANT_BY_COOKIE: Record<string, HomeFirstViewportVariant> = {
  a: 'control',
  b: 'variant',
};

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find(entry => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1);

/**
 * Exposure tracking of the first viewport test on the index page (LPOT-1761).
 * Sends `experiment_viewed` one time per page view when:
 * - the user is in the test (Akamai cookie `ab_index`),
 * - the cookie agrees with the rendered first viewport,
 * - the user gave the Mixpanel consent (also when it comes later),
 * - the market is Germany.
 * Use it only on the index page: the cookie is also there on other pages.
 */
export const useHomeFirstViewportExposure = () => {
  const renderedVariant = useHomeFirstViewportVariant();
  const { market } = useLocale();
  const hasMixpanelConsent = useConsent(Services.mixpanelProfiles);
  const isExposureTracked = useRef(false);

  useEffect(() => {
    if (isExposureTracked.current || !hasMixpanelConsent || market !== 'DE') {
      return;
    }

    const cookie = readCookie(ASSIGNMENT_COOKIE);
    const assignedVariant = cookie ? VARIANT_BY_COOKIE[cookie] : undefined;
    if (!assignedVariant || assignedVariant !== renderedVariant) {
      return;
    }

    isExposureTracked.current = true;
    trackExperimentViewed(EXPERIMENT_ID, assignedVariant);
  }, [hasMixpanelConsent, market, renderedVariant]);
};
